// src/App.js (updated routing)
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import SuperAdminRoute from "./components/SuperAdminRoute";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Signup from "./pages/SignUp";
import AcceptInvite from "./pages/AcceptInvite";
import Unsubscribe from "./pages/Unsubscribe";
import CustomerDashboard from "./pages/customer/CustomerDashboard";
import TeamManagement from "./pages/customer/TeamManagement";
import AiGovernanceSettings from "./pages/customer/Revenue/AiGovernanceSettings";
import DealValues from "./pages/customer/Revenue/DealValues";
import SuperAdminDashboard from "./pages/superadmin/SuperAdminDashboard";
import CustomersManagement from "./pages/superadmin/CustomersManagement";
import CompanyPool from "./pages/superadmin/CompanyPool";
import QAQueue from "./pages/superadmin/QAQueue";
import { useAuth } from "./contexts/AuthContext";
import PlanSelected from "./pages/PlanSelected";
import OnboardingSuccess from "./pages/OnboardingSuccess";
import FeedbackModal from "./components/FeedbackModal";
import BriefForm from "./pages/BriefForm";
import FormSubmissions from "./pages/superadmin/FormSubmissions";
import PartnerOutreach from "./pages/superadmin/PartnerOutreach";
import Exclusions from "./pages/superadmin/Exclusions";
import OnboardingQueue from "./pages/superadmin/OnboardingQueue";
import Notifications from "./pages/Notifications";
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/planpopup" element={<PlanSelected />} />
          <Route path="/accept-invite/:token" element={<AcceptInvite />} />
          <Route path="/onboarding/success" element={<OnboardingSuccess />} />
          <Route
            path="/onboarding/cancelled"
            element={<Navigate to="/signup" replace />}
          />
          {/* Unsubscribe */}
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Public brief intake form, no auth, token-authenticated via the
              URL itself. Must stay outside ProtectedRoute/AuthProvider's
              guard since the client filling it out has no account. */}
          <Route path="/brief/:token" element={<BriefForm />} />

          {/* Super Admin Routes */}
          <Route
            path="/super-admin"
            element={
              <SuperAdminRoute>
                <SuperAdminDashboard />
              </SuperAdminRoute>
            }
          />
          <Route
            path="/super-admin/customers"
            element={
              <SuperAdminRoute>
                <CustomersManagement />
              </SuperAdminRoute>
            }
          />
          <Route
            path="/super-admin/companies"
            element={
              <SuperAdminRoute>
                <CompanyPool />
              </SuperAdminRoute>
            }
          />
          <Route
            path="/super-admin/exclusions"
            element={
              <SuperAdminRoute>
                <Exclusions />
              </SuperAdminRoute>
            }
          />
          <Route
            path="/super-admin/onboarding"
            element={
              <SuperAdminRoute>
                <OnboardingQueue />
              </SuperAdminRoute>
            }
          />
          <Route
            path="/super-admin/qa"
            element={
              <SuperAdminRoute>
                <QAQueue />
              </SuperAdminRoute>
            }
          />
          <Route
            path="/super-admin/form-submissions"
            element={
              <SuperAdminRoute>
                <FormSubmissions />
              </SuperAdminRoute>
            }
          />
          <Route
            path="/super-admin/partner-outreach"
            element={
              <SuperAdminRoute>
                <PartnerOutreach />
              </SuperAdminRoute>
            }
          />
          {/* AI Governance Layer, the brief's Design Principle #2 ("AI Is
              Replaceable": provider/model per task type, cost caps,
              enable/disable) is an internal AXO control panel, explicitly
              "never partner-facing" per AiGovernanceSettings.jsx's own
              header comment, it is NOT part of the partner-facing Phase
              5A/5B revenue tooling that moved to the customer dashboard.
              It was dropped from here by mistake when that move happened,
              leaving it with no route anywhere in the app. Restored here,
              superadmin-only, same as every other /super-admin/* route. */}
          <Route
            path="/super-admin/ai-governance"
            element={
              <SuperAdminRoute>
                <AiGovernanceSettings />
              </SuperAdminRoute>
            }
          />
          {/* Deal values, the per-product £ figures behind pipeline
              estimates (see dealValueService.js). Nested under AI
              Governance in the nav since it's the same internal
              cost/commercial-numbers control panel, superadmin-only. */}
          <Route
            path="/super-admin/ai-governance/deal-values"
            element={
              <SuperAdminRoute>
                <DealValues />
              </SuperAdminRoute>
            }
          />

          {/* Phase 5 Revenue Intelligence (Sales Workspace, Pipeline, AI
              Performance, Revenue Forecast) lives in the customer
              dashboard as a single "Sales" section with tabs, see
              CustomerDashboard.jsx. The previous standalone
              /revenue-workspace/* routes duplicated that UI with no in-app
              link, so they were removed rather than kept as a second,
              orphaned surface. */}

          {/* Customer Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute requireCustomer={true}>
                <CustomerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/team"
            element={
              <ProtectedRoute requireCustomer={true}>
                <TeamManagement />
              </ProtectedRoute>
            }
          />
          {/* Notification centre — shared by customer users and super admins;
              the API scopes rows server-side. */}
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <Notifications />
              </ProtectedRoute>
            }
          />
          {/* Legacy route - redirects based on user type */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <SmartRedirect />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <FeedbackModal />
      </AuthProvider>
    </BrowserRouter>
  );
}

// Smart redirect based on user type
const SmartRedirect = () => {
  const { user } = useAuth();

  if (user?.isSuperAdmin) {
    return <Navigate to="/super-admin" replace />;
  }

  return <Navigate to="/dashboard" replace />;
};

export default App;
